import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSurveyOption,
  IWorkflowsSurveyQuestion,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import { IOption } from "../../../../../../../components/SelectDropdown";
import { useWardLabel } from "../../../../../../../hooks/useWardLabel";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import { SelectContainer, popupOptionsCss } from "../RuleComponent.styles";

interface ISurveyResponseFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  surveyOptions?: IWorkflowsSurveyOption[];
}

export function SurveyResponseFields({
  formik,
  surveyOptions,
}: ISurveyResponseFieldsProps) {
  const { t } = useTranslation();

  const wardLabel = useWardLabel();

  const shouldShowErrorMessage = (
    field: `conditions[${number}].${keyof IRuleCondition}`
  ): string => {
    if (field.startsWith("conditions")) {
      const key = field.split(".")[1] as keyof IRuleCondition;

      const touchedArray = formik.touched.conditions as
        | FormikTouched<IRuleCondition>[]
        | undefined;
      const errorArray = formik.errors.conditions as
        | FormikErrors<IRuleCondition>[]
        | undefined;

      if (touchedArray && errorArray) {
        const touched = touchedArray[0];
        const error = errorArray[0];

        return touched?.[key] && error?.[key] ? (error[key] as string) : "";
      }

      return "";
    }

    return "";
  };

  const surveyOptionDropdown = useMemo(() => {
    const optionsToDisplay: IOption[] =
      surveyOptions
        ?.filter((s) => s.surveyQuestionOptions.length > 0)
        .map((surveyOption) => {
          return {
            label: surveyOption.surveyDisplayName,
            value: surveyOption.surveyId,
            key: surveyOption.surveyId,
          };
        }) ?? [];

    return (
      <Select
        placeholder={t("workflows_rule_survey")}
        value={formik.values.conditions[0].surveyId}
        suffixIcon={<ArrowDown />}
        options={optionsToDisplay}
        popupClassName={popupOptionsCss()}
        notFoundContent={t("workflows_rule_noDataForWards", {
          wards: wardLabel.pluralLower,
        })}
        className={`${selectCss()} nopan nodrag`}
        onChange={(value) => {
          formik.setFieldValue("conditions[0].surveyId", value);
          formik.setFieldValue("conditions[0].surveyQuestionIds", undefined);
          formik.setFieldValue(
            "conditions[0].surveyQuestionOptionIds",
            undefined
          );
        }}
      />
    );
  }, [surveyOptions, formik.values.conditions[0].surveyId]);

  const surveyQuestionsDropdown = useMemo(() => {
    return (
      <Select
        placeholder={t("workflows_rule_questions")}
        mode="multiple"
        suffixIcon={<ArrowDown />}
        notFoundContent={t("workflows_rule_noData")}
        value={formik.values.conditions[0].surveyQuestionIds?.map(String)}
        options={
          surveyOptions
            ?.find((s) => s.surveyId === formik.values.conditions[0].surveyId)
            ?.surveyQuestionOptions.map((surveyQuestion) => {
              return {
                label: surveyQuestion.text,
                value: surveyQuestion.surveyQuestionId,
                key: uuidv4(),
              };
            }) ?? []
        }
        onChange={(value) => {
          formik.setFieldValue(
            "conditions[0].surveyQuestionIds",
            (value as string[])?.map(String)
          );
          formik.setFieldValue(
            "conditions[0].surveyQuestionOptionIds",
            undefined
          );
        }}
        className={`${selectCss()} nopan nodrag`}
        popupClassName={popupOptionsCss()}
        maxTagTextLength={24}
        maxTagCount={1}
      />
    );
  }, [
    surveyOptions,
    formik.values.conditions[0].surveyId,
    formik.values.conditions[0].surveyQuestionIds,
  ]);

  const handleQuestionAnswerOptions = () => {
    if (
      surveyOptions?.length === 0 ||
      formik.values.conditions[0].surveyQuestionIds === undefined
    ) {
      return [];
    }

    const groupedData = surveyOptions
      ?.find((s) => s.surveyId === formik.values.conditions[0].surveyId)
      ?.surveyQuestionOptions.filter((sq) =>
        formik.values.conditions[0].surveyQuestionIds?.includes(
          sq.surveyQuestionId
        )
      )
      ?.reduce(
        (
          accumulator: Record<string, DefaultOptionType>,
          surveyQuestion: IWorkflowsSurveyQuestion
        ) => {
          if (!accumulator[surveyQuestion.surveyQuestionId]) {
            accumulator[surveyQuestion.surveyQuestionId] = {
              label: surveyQuestion.text,
              title: surveyQuestion.surveyQuestionId,
              options: [],
            };
          }

          surveyQuestion.options.forEach((option) => {
            (
              accumulator[surveyQuestion.surveyQuestionId].options as IOption[]
            ).push({
              label: option.label,
              value: option.surveyQuestionOptionId,
              key: option.surveyQuestionOptionId,
            });
          });

          return accumulator;
        },
        {}
      );

    return Object.values(groupedData ?? {}) as DefaultOptionType[];
  };

  const surveyQuestionOptionsDropdown = useMemo(() => {
    return (
      <Select
        placeholder={t("workflows_rule_answers")}
        mode="multiple"
        value={formik.values.conditions[0].surveyQuestionOptionIds?.map(String)}
        suffixIcon={<ArrowDown />}
        options={handleQuestionAnswerOptions()}
        popupClassName={popupOptionsCss()}
        notFoundContent={t("workflows_rule_noData")}
        onChange={(value) => {
          formik.setFieldValue(
            "conditions[0].surveyQuestionOptionIds",
            (value as string[])?.map(String)
          );
        }}
        className={`${selectCss()} nopan nodrag`}
        maxTagTextLength={24}
        maxTagCount={1}
      />
    );
  }, [
    surveyOptions,
    formik.values.conditions[0].surveyQuestionIds,
    formik.values.conditions[0].surveyQuestionOptionIds,
  ]);

  return (
    <>
      <SelectContainer
        isError={shouldShowErrorMessage("conditions[0].surveyId") !== ""}
      >
        {surveyOptionDropdown}
        <ErrorMessage>
          {shouldShowErrorMessage("conditions[0].surveyId")}
        </ErrorMessage>
      </SelectContainer>

      {formik.values.conditions[0].surveyId !== undefined ? (
        <SelectContainer
          isError={
            shouldShowErrorMessage("conditions[0].surveyQuestionIds") !== ""
          }
        >
          <p>{t("workflows_rule_questions")}</p>
          {surveyQuestionsDropdown}
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].surveyQuestionIds")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.conditions[0].surveyQuestionIds !== undefined ? (
        <SelectContainer
          isError={
            shouldShowErrorMessage("conditions[0].surveyQuestionOptionIds") !==
            ""
          }
        >
          <p>{t("workflows_rule_answers")}</p>
          {surveyQuestionOptionsDropdown}
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].surveyQuestionOptionIds")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}
    </>
  );
}
